import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { useOrganization } from '../contexts/OrganizationContext'
import Card from './Card'
import Button from './Button'
import Input from './Input'
import Textarea from './Textarea'
import {
  TrendingUp,
  TrendingDown,
  Activity,
  Plus,
  Calendar,
  Target,
  BarChart3,
} from 'lucide-react'
import {
  getCarePlanProgress,
  createProgressRecord,
  getProgressDashboard,
} from '../services/carePlanProgress'
import { formatDate } from '../utils/helpers'

const CarePlanProgressDashboard = ({ carePlanId, clientId }) => {
  const [progressRecords, setProgressRecords] = useState([])
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({
    progressDate: new Date().toISOString().slice(0, 10),
    notes: '',
    adherenceScore: '',
    metrics: {},
  })
  const [submitting, setSubmitting] = useState(false)
  const organizationId = useOrganizationId()
  const { canWrite } = useOrganization()

  useEffect(() => {
    if (carePlanId && organizationId) {
      fetchProgress()
    }
  }, [carePlanId, organizationId])

  const fetchProgress = async () => {
    if (!carePlanId || !organizationId) return
    try {
      setLoading(true)
      const [records, dashboard] = await Promise.all([
        getCarePlanProgress(carePlanId, organizationId),
        getProgressDashboard(carePlanId, organizationId),
      ])
      setProgressRecords(records)
      setDashboardData(dashboard)
    } catch (error) {
      console.error('Error fetching progress:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!carePlanId || !clientId || !organizationId) return

    try {
      setSubmitting(true)
      await createProgressRecord({
        organizationId,
        carePlanId,
        clientId,
        progressDate: formData.progressDate,
        notes: formData.notes,
        adherenceScore: formData.adherenceScore ? parseInt(formData.adherenceScore) : null,
        metrics: formData.metrics,
      })

      setFormData({
        progressDate: new Date().toISOString().slice(0, 10),
        notes: '',
        adherenceScore: '',
        metrics: {},
      })
      setShowForm(false)
      fetchProgress()
    } catch (error) {
      console.error('Error creating progress record:', error)
      alert(error.message || 'Failed to save progress')
    } finally {
      setSubmitting(false)
    }
  }

  const getAdherenceColor = (score) => {
    if (score >= 80) return 'text-green-600'
    if (score >= 60) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getAdherenceLabel = (score) => {
    if (score >= 80) return 'Excellent'
    if (score >= 60) return 'Good'
    if (score >= 40) return 'Fair'
    return 'Poor'
  }

  if (loading) {
    return <div className="text-sm text-gray-500">Loading progress...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <BarChart3 size={20} />
          Progress Tracking
        </h3>
        {canWrite('care_plans') && (
          <Button variant="secondary" size="sm" onClick={() => setShowForm(true)}>
            <Plus size={16} className="mr-1" />
            Record Progress
          </Button>
        )}
      </div>

      {/* Dashboard Stats */}
      {dashboardData && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card variant="soft" className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Records</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardData.totalRecords}</p>
              </div>
              <Activity size={24} className="text-primary-500" />
            </div>
          </Card>
          <Card variant="soft" className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Average Adherence</p>
                <p
                  className={`text-2xl font-bold ${
                    dashboardData.averageAdherence
                      ? getAdherenceColor(dashboardData.averageAdherence)
                      : 'text-gray-400'
                  }`}
                >
                  {dashboardData.averageAdherence
                    ? `${Math.round(dashboardData.averageAdherence)}%`
                    : 'N/A'}
                </p>
                {dashboardData.averageAdherence && (
                  <p className="text-xs text-gray-500">
                    {getAdherenceLabel(dashboardData.averageAdherence)}
                  </p>
                )}
              </div>
              <Target size={24} className="text-secondary-500" />
            </div>
          </Card>
          <Card variant="soft" className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Latest Record</p>
                <p className="text-sm font-semibold text-gray-900">
                  {dashboardData.latestRecord
                    ? formatDate(dashboardData.latestRecord.progress_date)
                    : 'None'}
                </p>
              </div>
              <Calendar size={24} className="text-accent-500" />
            </div>
          </Card>
        </div>
      )}

      {/* Progress Form */}
      {showForm && canWrite('care_plans') && (
        <Card variant="soft" className="p-6">
          <h4 className="font-semibold text-gray-900 mb-4">Record Progress</h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="date"
              label="Progress Date"
              value={formData.progressDate}
              onChange={(e) => setFormData({ ...formData, progressDate: e.target.value })}
              required
            />
            <Textarea
              label="Progress Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Document progress, observations, challenges..."
              rows={4}
            />
            <Input
              type="number"
              label="Adherence Score (0-100)"
              value={formData.adherenceScore}
              onChange={(e) => setFormData({ ...formData, adherenceScore: e.target.value })}
              placeholder="e.g. 85"
              min="0"
              max="100"
            />
            <div className="flex gap-3">
              <Button type="submit" disabled={submitting} className="flex-1">
                {submitting ? 'Saving...' : 'Save Progress'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowForm(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Progress Timeline */}
      {progressRecords.length === 0 ? (
        <Card variant="soft" className="p-6 text-center">
          <Activity size={32} className="mx-auto mb-2 text-gray-400" />
          <p className="text-sm text-gray-500">No progress records yet</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {progressRecords.map((record, index) => {
            const prevRecord = progressRecords[index + 1]
            const adherenceChange =
              prevRecord && record.adherence_score && prevRecord.adherence_score
                ? record.adherence_score - prevRecord.adherence_score
                : null

            return (
              <Card key={record.id} variant="soft" className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-gray-900">
                      {formatDate(record.progress_date)}
                    </p>
                    {record.recorded_by_user && (
                      <p className="text-xs text-gray-500">
                        Recorded by: {record.recorded_by_user.email}
                      </p>
                    )}
                  </div>
                  {record.adherence_score !== null && (
                    <div className="text-right">
                      <div className="flex items-center gap-2">
                        {adherenceChange !== null && adherenceChange !== 0 && (
                          <span
                            className={`flex items-center gap-1 text-xs ${
                              adherenceChange > 0 ? 'text-green-600' : 'text-red-600'
                            }`}
                          >
                            {adherenceChange > 0 ? (
                              <TrendingUp size={14} />
                            ) : (
                              <TrendingDown size={14} />
                            )}
                            {Math.abs(adherenceChange)}%
                          </span>
                        )}
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-semibold ${getAdherenceColor(
                            record.adherence_score
                          )} bg-opacity-10`}
                        >
                          {record.adherence_score}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                {record.progress_notes && (
                  <p className="text-sm text-gray-700 mb-2">{record.progress_notes}</p>
                )}
                {record.metrics && Object.keys(record.metrics).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <p className="text-xs font-semibold text-gray-500 mb-1">Metrics:</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(record.metrics).map(([key, value]) => (
                        <span
                          key={key}
                          className="px-2 py-1 rounded bg-gray-100 text-gray-700 text-xs"
                        >
                          {key}: {value}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default CarePlanProgressDashboard

